import { cva } from 'class-variance-authority';
import type { JSX } from 'react';
import type { CvaVariantOptions } from '~/utils/cva';
import { cn } from '~/utils';

export type VariantOptions = CvaVariantOptions<typeof VARIANTS>;

const Title: React.FC<{
    children: React.ReactNode;
    /** @default 'xl' */
    size?: VariantOptions['size'];
    /** @default 'xbold' */
    weight?: VariantOptions['weight'];
    /** @default 'h2' */
    as?: Extract<keyof JSX.IntrinsicElements, `h${number}` | 'legend'>;
    color?: VariantOptions['color'];
    className?: string;
    style?: React.CSSProperties;
    /** @default 'left' */
    alignment?: VariantOptions['alignment'];
    id?: string;
    onClick?: React.MouseEventHandler<HTMLHeadingElement>;
}> = ({ children, size, as: Component = 'h2', color, weight, className, alignment, ...props }) => (
    <Component
        {...props}
        className={cn(
            VARIANTS({
                size,
                weight,
                color,
                alignment,
            }),
            ['text-balance', className],
        )}
    >
        {children}
    </Component>
);

export default Title;

// Todo: add mobile sizes
const VARIANTS = cva('', {
    variants: {
        size: {
            '7xl': 'text-[1.75rem] sm:text-[2.5rem] md:text-[3.25rem] lg:text-[5.625rem]',
            '6xl': 'text-[1.75rem] sm:text-[2.5rem] md:text-[2.5rem] lg:text-[4.375rem]',
            '5xl': 'text-[1.375rem] sm:text-[1.75rem] md:text-[2rem] lg:text-[2.5rem] xl:text-[3.25rem]',
            '4xl': 'text-[1.75rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[2.5rem] xl:text-[3.25rem]',
            '3xl': 'text-[1.125rem] sm:text-[1.375rem] md:text-[1.375rem] lg:text-[2rem] xl:text-[2.5rem]',
            '2xl': 'text-[1.125rem] sm:text-[1.375rem] md:text-[1.375rem] lg:text-[1.75rem] xl:text-[2rem]',
            xl: 'text-[1.125rem] sm:text-[1.375rem] md:text-[1.375rem] lg:text-[1.375rem] xl:text-[1.75rem]',
            lg: 'lg:text-[1.125rem]',
            base: 'text-base',
        },
        weight: {
            xbold: 'leading-[100%] font-extrabold',
            bold: 'leading-[140%] font-bold',
            medium: 'leading-[160%] font-medium',
            regular: 'leading-[160%] font-normal',
        },
        color: {
            light: 'text-white',
            dark: 'text-primary',
        },
        alignment: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        },
        headerTags: {
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
        },
    },
    defaultVariants: {
        color: 'dark',
        size: 'xl',
        weight: 'xbold',
    },
});
